import { useState, useCallback, useEffect, useMemo } from 'react';
import { uploadAttachment } from '../services/api';
import { validateFile } from '../utils/validation/fileValidation';
import { autoCorrectImageOrientation } from '../utils/imageOrientation';
import toast from 'react-hot-toast';
import logger from '../utils/logger';

/**
 * Custom hook for handling file uploads, parameterized by SCOPE.
 *
 * Each scope owns its own attachment bucket so unrelated upload contexts
 * (support tickets vs. community announcements vs. anything we add later)
 * cannot leak state into each other. Storage keys are derived from scope,
 * and persistence is opt-in.
 *
 * @param {object} [opts]
 * @param {string} [opts.scope='ticket']
 *   Bucket name. Stored as `<scope>_attachments` in localStorage when
 *   `persist` is true. Different scopes = fully isolated state.
 * @param {boolean} [opts.persist=true]
 *   When true (default), attachments survive page reloads via localStorage
 *   (ticket flow needs this — multi-message accumulator). When false,
 *   the bucket is in-memory only (correct for one-shot flows like
 *   community announcements where leftover state is a bug).
 *
 * Defaults preserve the original ticket-flow behavior byte-for-byte so
 * existing callers (and tests) that invoke `useFileUpload()` with no
 * args keep working unchanged.
 *
 * @returns {{
 *   attachments: Array,
 *   uploading: boolean,
 *   uploadProgress: number,
 *   handleFileUpload: (file: File) => Promise<void>,
 *   removeAttachment: (index: number) => void,
 *   clearAttachments: () => void,
 * }}
 */
export const useFileUpload = (opts = {}) => {
  const { scope = 'ticket', persist = true } = opts;

  // Storage keys are derived from scope so two scopes never collide.
  // Memoized so the useEffect deps below stay stable.
  const { storageKey, clearFlagKey } = useMemo(() => ({
    storageKey: `${scope}_attachments`,
    clearFlagKey: `${scope}_attachments_should_clear`,
  }), [scope]);

  // Initialize from localStorage when persistence is on; otherwise start
  // empty in memory. Restore semantics matched the original ticket flow:
  //   - if the "should_clear" flag is set, wipe and start fresh
  //   - else, restore the saved attachments
  const [attachments, setAttachments] = useState(() => {
    if (!persist) return [];
    try {
      const shouldClear = localStorage.getItem(clearFlagKey);
      if (shouldClear === 'true') {
        logger.info('FileUpload', `Clearing attachments on init [scope=${scope}]`);
        localStorage.removeItem(storageKey);
        localStorage.removeItem(clearFlagKey);
        return [];
      }
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        logger.info('FileUpload', `Restored attachments from localStorage [scope=${scope}]`, {
          count: parsed.length,
        });
        return parsed;
      }
    } catch (error) {
      logger.error('FileUpload', `Failed to restore attachments [scope=${scope}]`, error);
    }
    return [];
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Mirror state → localStorage only when persistence is enabled.
  // When persist=false we never touch storage at all, so other scopes
  // (or other apps using the same key namespace) can never see our data.
  useEffect(() => {
    if (!persist) return;
    try {
      if (attachments.length > 0) {
        localStorage.setItem(storageKey, JSON.stringify(attachments));
        logger.debug('FileUpload', `Saved attachments to localStorage [scope=${scope}]`, {
          count: attachments.length,
        });
      } else {
        localStorage.removeItem(storageKey);
        logger.debug('FileUpload', `Cleared attachments from localStorage [scope=${scope}]`);
      }
    } catch (error) {
      logger.error('FileUpload', `Failed to save attachments [scope=${scope}]`, error);
    }
  }, [attachments, persist, storageKey, scope]);

  /**
   * Upload a file and add it to attachments
   * Auto-corrects image orientation based on EXIF data for mobile photos
   * @param {File} file - File to upload
   */
  const handleFileUpload = useCallback(async (file) => {
    // Validate file
    const validation = validateFile(file);
    if (!validation.valid) {
      toast.error(validation.error);
      logger.warn('FileUpload', 'File validation failed', {
        filename: file.name,
        error: validation.error,
      });
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      logger.info('FileUpload', 'Starting file upload', {
        filename: file.name,
        size: file.size,
        type: validation.fileType,
        scope,
      });

      let fileToUpload = file;

      if (validation.fileType === 'image') {
        try {
          const corrected = await autoCorrectImageOrientation(file);
          fileToUpload = corrected.file;
          if (corrected.wasCorrected) {
            logger.info('FileUpload', 'Image orientation auto-corrected', {
              filename: file.name,
            });
          }
        } catch (orientationError) {
          logger.warn('FileUpload', 'Failed to auto-correct orientation, using original file', {
            filename: file.name,
            error: orientationError.message,
          });
        }
      }

      const response = await uploadAttachment(fileToUpload, (progress) => {
        setUploadProgress(progress);
      });

      const attachment = {
        type: validation.fileType,
        url: response.url,
        filename: file.name,
        size: file.size,
        uploaded_at: new Date().toISOString(),
      };

      setAttachments(prev => [...prev, attachment]);

      toast.success(`${file.name} uploaded successfully`);
      logger.success('FileUpload', 'File uploaded successfully', {
        filename: file.name,
        url: response.url,
      });
    } catch (error) {
      let errorMessage = error.response?.data?.detail || 'Failed to upload file. Please try again.';
      if (error.response?.status === 413) {
        const sizeMB = (file.size / 1024 / 1024).toFixed(1);
        errorMessage = `File too large (${sizeMB}MB). Backend server limit exceeded. Please use a smaller file or contact support to increase upload limits.`;
      }
      toast.error(errorMessage);
      logger.error('FileUpload', 'File upload failed', {
        filename: file.name,
        fileSize: `${(file.size / 1024 / 1024).toFixed(1)}MB`,
        statusCode: error.response?.status,
        error: error.message,
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [scope]);

  const removeAttachment = useCallback((index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    logger.info('FileUpload', `Attachment removed [scope=${scope}]`, { index });
  }, [scope]);

  /**
   * Clear all attachments. When persistence is on, sets a flag so the next
   * mount starts fresh (ticket-completion semantics). When persistence is
   * off, there's nothing to flag — state simply empties.
   */
  const clearAttachments = useCallback(() => {
    setAttachments([]);
    if (persist) {
      localStorage.setItem(clearFlagKey, 'true');
    }
    logger.info('FileUpload', `All attachments cleared [scope=${scope}]`);
  }, [persist, clearFlagKey, scope]);

  return {
    attachments,
    uploading,
    uploadProgress,
    handleFileUpload,
    removeAttachment,
    clearAttachments,
  };
};
